import { useState, useRef, useEffect } from 'react';
import { useClients } from '../hooks/useClients.js';
import { useClientStore } from '../store/client.store.js';
import { useAuthStore } from '../store/auth.store.js';
import Spinner from './Spinner.jsx';

export default function ClientSelectorDropdown() {
  const user = useAuthStore((s) => s.user);
  const { selectedClientId, selectedClientCode, setClient, clearClient } = useClientStore();
  const { data: clients, isLoading } = useClients();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // Solo para superadmin
  if (user?.role !== 'superadmin') return null;

  // Cerrar al click fuera
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = (clients || []).filter((c) =>
    !search || c.code.toLowerCase().includes(search.toLowerCase()) ||
    c.business_name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (client) => {
    setClient({ id: client.id, code: client.code, name: client.business_name });
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors border ${
          selectedClientId
            ? 'bg-accent/15 border-accent/40 text-accent hover:bg-accent/20'
            : 'bg-bg-elevated border-border-strong text-slate-400 border-dashed hover:text-slate-200'
        }`}
      >
        <span className="font-mono font-medium">
          {selectedClientCode || '·· Sin cliente'}
        </span>
        <svg className="w-3 h-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-72 bg-bg-surface border border-border rounded-lg shadow-xl z-50">
          <div className="p-2 border-b border-border">
            <input
              autoFocus
              className="input-base py-1.5 text-sm"
              placeholder="Buscar cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {isLoading ? (
              <div className="flex justify-center py-4"><Spinner /></div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-slate-500 text-sm py-4">Sin resultados</p>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleSelect(c)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-bg-elevated flex items-center gap-2 transition-colors ${
                    selectedClientId === c.id ? 'text-accent' : 'text-slate-200'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${selectedClientId === c.id ? 'bg-accent' : 'bg-transparent border border-slate-600'}`} />
                  <span className="font-mono font-medium text-xs text-slate-400 w-20 shrink-0">{c.code}</span>
                  <span className="truncate">{c.business_name}</span>
                </button>
              ))
            )}
          </div>
          {selectedClientId && (
            <div className="p-2 border-t border-border">
              <button onClick={() => { clearClient(); setOpen(false); }}
                className="w-full text-xs text-slate-400 hover:text-white py-1 hover:bg-bg-elevated rounded transition-colors">
                Limpiar selección
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
